<template>
  <div class="bar-card">
    <div class="header">
      <h3>{{ title }}</h3>
      <slot name="right"></slot>
    </div>

    <div class="chart-wrap" ref="wrapRef" @mouseleave="hideTip">
      <svg :viewBox="`0 0 ${W} ${H}`" class="chart" role="img" aria-label="Gráfico de barras - notas por mês">
        <!-- área do gráfico -->
        <g :transform="`translate(${m.left},${m.top})`">
          <!-- linhas de grade -->
          <g class="grid">
            <line v-for="t in yTicks" :key="`g-${t}`"
                  :x1="0" :x2="innerW" :y1="yScale(t)" :y2="yScale(t)" />
          </g>

          <!-- eixo Y (valores) -->
          <g class="axis-y">
            <text v-for="t in yTicks" :key="`y-${t}`"
                  :x="-6" :y="yScale(t) + 3" text-anchor="end">{{ formatAbbr(t) }}</text>
          </g>

          <!-- barras -->
          <g class="bars">
            <template v-for="(d, i) in series" :key="`b-${i}`">
              <rect
                :x="xScale(i)"
                :y="yScale(d.v)"
                :width="barW"
                :height="Math.max(0, innerH - yScale(d.v))"
                :fill="barColor"
                rx="2"
                @mousemove="showTip($event, d)"
              />
            </template>
          </g>

          <!-- eixo X (meses) -->
          <g class="axis-x">
            <text v-for="(d, i) in series" :key="`x-${i}`"
                  :x="xScale(i) + barW/2" :y="innerH + 20" text-anchor="middle">
              {{ d.lbl }}
            </text>
          </g>
        </g>
      </svg>

      <!-- tooltip -->
      <div v-if="tip.show" class="tooltip" :style="{ left: tip.x + 'px', top: tip.y + 'px' }">
        <div class="tip-title">{{ tip.data.lblFull }}</div>
        <div class="tip-value">{{ formatBRL(tip.data.v) }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'

const props = defineProps({
  data: { type: Array, required: true }, // [{ ano, mes, vlnotas }]
  title: { type: String, default: 'Notas por mês' },
  barColor: { type: String, default: '#2563EB' }, // azul
})

/* --- dimensões SVG --- */
const W = 760, H = 340
const m = { top: 16, right: 12, bottom: 40, left: 56 }
const innerW = W - m.left - m.right
const innerH = H - m.top - m.bottom

/* --- dados e ordenação --- */
const monthFmt = (m) =>
  new Date(2000, m - 1, 1).toLocaleString('pt-BR', { month: 'short' }).replace('.', '')
const series = computed(() => {
  const sorted = [...(props.data || [])]
    .sort((a,b) => a.ano !== b.ano ? a.ano - b.ano : a.mes - b.mes)
    .map(d => ({
      lbl: `${monthFmt(d.mes)}/${String(d.ano).slice(-2)}`,
      lblFull: `${monthFmt(d.mes)} de ${d.ano}`,
      v: Number(d.vlnotas) || 0
    }))
  return sorted
})

/* --- escalas --- */
const maxV = computed(() => {
  const m = Math.max(0, ...series.value.map(d => d.v))
  // margem de 10% (mínimo absoluto para não colar no topo)
  return m <= 0 ? 1 : Math.ceil(m * 1.1)
})
const xStep = computed(() => innerW / Math.max(1, series.value.length))
const gap = 8
const barW = computed(() => Math.max(2, xStep.value - gap))
const xScale = (i) => i * xStep.value + (gap / 2)
const yScale = (v) => innerH - (v / maxV.value) * innerH

/* --- ticks Y --- */
const yTicks = computed(() => {
  const n = 5
  const step = maxV.value / n
  return Array.from({ length: n + 1 }, (_, i) => Math.round(i * step))
})

/* --- tooltip --- */
const wrapRef = ref(null)
const tip = reactive({ show: false, x: 0, y: 0, data: { lblFull: '', v: 0 } })
function showTip(evt, d) {
  const r = wrapRef.value?.getBoundingClientRect()
  tip.show = true
  tip.x = (evt.clientX - (r?.left || 0)) + 8
  tip.y = (evt.clientY - (r?.top || 0)) - 36
  tip.data = d
}
function hideTip() { tip.show = false }

/* --- helpers --- */
function formatBRL(n) {
  return (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}
function formatAbbr(n) {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1_000_000_000) return (v/1_000_000_000).toFixed(1).replace('.', ',') + ' bi'
  if (Math.abs(v) >= 1_000_000)     return (v/1_000_000).toFixed(1).replace('.', ',') + ' mi'
  if (Math.abs(v) >= 1_000)         return (v/1_000).toFixed(1).replace('.', ',') + ' mil'
  return v.toLocaleString('pt-BR')
}
</script>

<style scoped>
.bar-card{
  padding:16px;border:1px solid #e5e7eb;border-radius:16px;background:#fff;
  box-shadow:0 1px 2px rgba(0,0,0,.05)
}
.header{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
.header h3{margin:0;font-size:16px;font-weight:700;color:#111827}
.chart-wrap{position:relative;overflow:visible}
.chart{width:100%;height:auto;display:block}

.grid line{stroke:#e5e7eb;stroke-width:1}
.axis-y text{fill:#6b7280;font-size:12px}
.axis-x text{fill:#6b7280;font-size:12px}

.bars rect{transition:opacity .15s ease, y .2s ease, height .2s ease}
.bars rect:hover{opacity:.85}

.tooltip{
  position:absolute;pointer-events:none;background:#111827;color:#fff;
  padding:8px 10px;border-radius:8px;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,.18)
}
.tooltip .tip-title{opacity:.9;margin-bottom:4px}
.tooltip .tip-value{font-weight:700}
</style>
